import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  LinkIcon,
  DocumentTextIcon,
  UserGroupIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  MapIcon,
  SparklesIcon,
  CpuChipIcon,
  Square3Stack3DIcon,
  ChevronDownIcon,
  PlusIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  UserCircleIcon,
  CreditCardIcon,
  BeakerIcon,
  DocumentChartBarIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useDomain } from '../../contexts/DomainContext';

const DashboardLayout = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { domains, selectedDomain, selectDomain, addDomain, canAddDomain, domainLimit, loading: domainsLoading } = useDomain();
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const [newDomainInput, setNewDomainInput] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const domainDropdownRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) {
        setShowDomainDropdown(false);
        setAddingDomain(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddDomain = async () => {
    if (!newDomainInput.trim()) return;
    const result = await addDomain(newDomainInput.trim());
    if (result.success) {
      setNewDomainInput('');
      setAddingDomain(false);
    } else {
      alert(result.error || 'Failed to add domain');
    }
  };
  const [expandedMenus, setExpandedMenus] = useState(['seo', 'ai-tools']);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: HomeIcon,
      path: '/dashboard',
      badge: null
    },
    {
      id: 'geo',
      title: 'GEO',
      icon: GlobeAltIcon,
      expandable: true,
      badge: null,
      children: [
        {
          title: 'Dashboard',
          path: '/geo',
          description: 'GEO visibility overview',
          icon: ChartBarIcon
        },
        {
          title: 'Tracking',
          path: '/geo/tracking',
          description: 'Track AI mentions',
          icon: ChartBarIcon
        },
        {
          title: 'AI Prompts',
          path: '/geo/ai-prompts',
          description: 'Manage tracked prompts',
          icon: DocumentTextIcon
        }
      ]
    },
    {
      id: 'ai-tools',
      title: 'AI-Powered Tools',
      icon: CpuChipIcon,
      expandable: true,
      badge: null,
      children: [
        {
          title: 'AI Search Visibility',
          path: '/ai-search-visibility',
          description: 'Track AI search presence',
          icon: SparklesIcon
        },
        {
          title: 'SERP Analysis',
          path: '/serp-analysis',
          description: 'Analyze search results',
          icon: ChartBarIcon
        },
        {
          title: 'AI Content Writer',
          path: '/content-writer',
          description: 'Generate SEO content',
          icon: PencilSquareIcon,
          badge: 'AI'
        }
      ]
    },
    {
      id: 'seo',
      title: 'SEO Tools',
      icon: MagnifyingGlassIcon,
      expandable: true,
      children: [
        {
          title: 'Domain Analysis',
          path: '/domain-analysis',
          description: 'Analyze any domain',
          icon: GlobeAltIcon
        },
        {
          title: 'Keyword Research',
          path: '/keyword-research',
          description: 'Find the best keywords',
          icon: MagnifyingGlassIcon
        },
        {
          title: 'Competitive Analysis',
          path: '/competitive-analysis',
          description: 'Analyze competitors',
          icon: UserGroupIcon
        },
        {
          title: 'Backlink Explorer',
          path: '/backlink-explorer',
          description: 'Explore backlinks',
          icon: LinkIcon
        },
        {
          title: 'Site Audit',
          path: '/site-audit',
          description: 'Technical SEO audit',
          icon: BeakerIcon,
          badge: 'NEW'
        },
        {
          title: 'Rank Tracking',
          path: '/rank-tracking',
          description: 'Track keyword rankings',
          icon: ChartBarIcon
        }
      ]
    },
    {
      id: 'content',
      title: 'Content Marketing',
      icon: DocumentTextIcon,
      expandable: true,
      children: [
        {
          title: 'Content Analyzer',
          path: '/content-analyzer',
          description: 'Analyze content performance'
        },
        {
          title: 'Topic Research',
          path: '/topic-research',
          description: 'Find trending topics'
        },
        {
          title: 'Content Audit',
          path: '/content-audit',
          description: 'Audit existing content'
        }
      ]
    },
    {
      id: 'local',
      title: 'Local SEO',
      icon: MapIcon,
      expandable: true,
      children: [
        {
          title: 'Local Rankings',
            badge: 'NEW',
          path: '/local-rankings',
          description: 'Track local positions'
        },
        {
          title: 'Listing Management',
          path: '/listing-management',
          description: 'Manage business listings'
        },
        {
          title: 'Review Management',
            badge: 'Coming Soon',
          path: '/review-management',
          description: 'Monitor reviews'
        }
      ]
    },
    {
      id: 'tools',
      title: 'Tools & Reports',
      icon: WrenchScrewdriverIcon,
      expandable: true,
      children: [
        {
          title: 'My Reports',
          path: '/reports',
          description: 'Custom reports'
        },
        {
          title: 'Projects',
          path: '/projects',
          description: 'Manage projects'
        }
      ]
    },
    {
      title: 'Social Media Insights',
      path: '/social-media-insights',
      icon: ShareIcon,
      description: 'Analyze social presence',
      badge: 'NEW'
    },
    {
      title: 'Integrations',
      path: '/integrations',
      icon: Square3Stack3DIcon,
      description: 'Connect your tools',
      badge: 'NEW'
    },
  ];

  const isActivePath = (path) => location.pathname === path;
  const isParentActive = (item) => {
    if (item.children) {
      return item.children.some(child => isActivePath(child.path));
    }
    return false;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <div className={`fixed inset-y-0 left-0 z-50 w-80 bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out lg:fixed lg:translate-x-0 lg:h-screen ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-700 bg-gray-900/50">
          <div className="flex items-center">
            <div className="relative">
              <Square3Stack3DIcon className="h-8 w-8 text-blue-500" />
              <div className="absolute -inset-1 bg-blue-500/20 rounded-full blur-md"></div>
            </div>
            <span className="ml-3 text-xl font-bold text-white">SERPfinity™</span>
            <span className="ml-2 px-2 py-0.5 text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full font-semibold">
              PRO
            </span>
          </div>
        </div>

        {/* Project Selector */}
            <div className="px-4 py-3 border-b border-gray-700/50 bg-gray-800/30 relative" ref={domainDropdownRef}>
              <button onClick={() => setShowDomainDropdown(!showDomainDropdown)} className="w-full px-3 py-2 text-left bg-gray-800/50 rounded-lg hover:bg-gray-700/50 transition-all duration-200 border border-gray-700/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Current Domain ({domains.length}/{domainLimit})</p>
                    <p className="text-sm font-medium text-white truncate">{selectedDomain || 'Select a domain'}</p>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${showDomainDropdown ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {showDomainDropdown && (
                <div className="absolute left-4 right-4 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  {domains.length > 0 ? domains.map((d) => (
                    <button key={d.id} onClick={() => { selectDomain(d.domain); setShowDomainDropdown(false); }} className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700/50 ${selectedDomain === d.domain ? 'bg-blue-600/20 text-blue-400' : 'text-gray-300'}`}>
                      {d.domain}
                    </button>
                  )) : (
                    <p className="px-3 py-2 text-xs text-gray-500">No domains added yet</p>
                  )}
                  {canAddDomain && !addingDomain && (
                    <button onClick={() => setAddingDomain(true)} className="w-full px-3 py-2 text-left text-sm text-green-400 hover:bg-gray-700/50 border-t border-gray-700 flex items-center gap-2">
                      <PlusIcon className="h-4 w-4" /> Add Domain
                    </button>
                  )}
                  {addingDomain && (
                    <div className="p-2 border-t border-gray-700">
                      <input type="text" value={newDomainInput} onChange={(e) => setNewDomainInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()} placeholder="example.com" className="w-full px-2 py-1 text-sm bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 mb-1" autoFocus />
                      <div className="flex gap-1">
                        <button onClick={handleAddDomain} className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                        <button onClick={() => { setAddingDomain(false); setNewDomainInput(''); }} className="flex-1 px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600">Cancel</button>
                      </div>
                    </div>
                  )}
                  {!canAddDomain && domains.length >= domainLimit && (
                    <p className="px-3 py-2 text-xs text-yellow-500 border-t border-gray-700">Domain limit reached ({domainLimit}). Upgrade for more.</p>
                  )}
                </div>
              )}
            </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 overflow-y-auto">
          {menuItems.map(item => (
            <div key={item.id} className="mb-1">
              {item.expandable ? (
                <>
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 group
                      ${isParentActive(item) 
                        ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white border-l-2 border-blue-500' 
                        : 'text-gray-300 hover:bg-gray-700/30 hover:text-white'}`}
                  >
                    <div className="flex items-center">
                      <item.icon className="h-5 w-5 mr-3" />
                      <span className="text-sm font-medium">{item.title}</span>
                      {item.badge && (
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-semibold
                          ${item.badge === 'NEW' ? 'bg-green-500/20 text-green-400' : 
                            item.badge === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-blue-500/20 text-blue-400'}`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <ChevronRightIcon 
                      className={`h-4 w-4 transition-transform ${expandedMenus.includes(item.id) ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {expandedMenus.includes(item.id) && (
                    <div className="mt-1 ml-8">
                      {item.children.map(child => (
                        <Link
                          key={child.path}
                          to={child.path}
                    onClick={closeSidebar}
                          className={`flex items-center px-3 py-2 rounded-lg mb-0.5 transition-all duration-200
                            ${isActivePath(child.path)
                              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/20'
                              : 'text-gray-400 hover:text-white hover:bg-gray-700/30'}`}
                        >
                          {child.icon && <child.icon className="h-4 w-4 mr-2" />}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{child.title}</p>
                            {child.description && (
                              <p className="text-xs opacity-75">{child.description}</p>
                            )}
                          </div>
                          {child.badge && (
                            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-semibold
                              ${child.badge === 'AI' ? 'bg-purple-500/20 text-purple-400' : 
                                child.badge === 'NEW' ? 'bg-green-500/20 text-green-400' :
                                'bg-blue-500/20 text-blue-400'}`}>
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                    onClick={closeSidebar}
                  className={`flex items-center px-3 py-2 rounded-lg transition-all duration-200 group
                    ${isActivePath(item.path)
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/20'
                      : 'text-gray-300 hover:bg-gray-700/30 hover:text-white'}`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  <span className="text-sm font-medium">{item.title}</span>
                  {item.badge && (
                    <span className="ml-auto px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full font-semibold">
                      {item.badge}
                    </span>
                  )}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Section */}
        <div className="px-4 py-3 border-t border-gray-700/50">
          <div className="space-y-2">
            <Link
              to="/pricing"
                    onClick={closeSidebar}
              className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700/30 rounded-lg transition-all duration-200"
            >
              <CreditCardIcon className="h-5 w-5 mr-3" />
              Upgrade Plan
            </Link>
            <Link
              to="/help"
                    onClick={closeSidebar}
              className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700/30 rounded-lg transition-all duration-200"
            >
              <QuestionMarkCircleIcon className="h-5 w-5 mr-3" />
              Help & Support
            </Link>
          </div>

          {/* Copyright */}
          <div className="mt-3 pt-3 border-t border-gray-700/50">
            <p className="text-xs text-gray-500 text-center">
              © {new Date().getFullYear()} SERPfinity™
            </p>
            <p className="text-xs text-gray-500 text-center">
              All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-80">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 shadow-sm sticky top-0 z-30">
            {/* Mobile hamburger button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          <div className="flex items-center">
            {title && (
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
                {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <BellIcon className="h-6 w-6" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName || user?.email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.plan || 'Free'} Plan
                  </p>
                </div>
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <UserCircleIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Profile Settings
                  </Link>
                  <Link
                    to="/billing"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <CreditCardIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Billing & Plans
                  </Link>
                  <hr className="my-1" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-gray-50">
          <div className="p-3 sm:p-4 lg:p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;